import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ActivityCard from 'balkerne-components/dashboard/ActivityCard'
import DashboardMap from '../components/DashboardMap'
import SavedDrafts from 'balkerne-components/dashboard/SavedDrafts'
import AlertsCard from 'balkerne-components/dashboard/AlertsCard'
import { useDispatch } from 'react-redux'
import systemActions from '../store/system'
import siteMap from '../siteMap'
import Page from '../components/Page'
import { useLocation } from '../hooks/locations'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { FeatureGate, PropertyPermissions, usePermissions } from 'balkerne-core/roles'
import api from 'balkerne-core/api'
import { useSnackbar } from 'notistack'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import PropertyClimateHazardsCard from 'balkerne-components/dashboard/PropertyClimateHazardsCard'

const Property = () => {
  const history = useHistory()
  const { id: propertyId } = useParams()
  const { data: property } = useLocation(propertyId)
  const dispatch = useDispatch()
  const { possible } = usePermissions()
  const { enqueueSnackbar } = useSnackbar()
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // property: object (example)
  // {
  //     "id": 9635,
  //     "organisation_id": 1,
  //     "name": "82 Manchester Road",
  //     "type": {
  //         "id": "196c16a3-cc9d-5f28-90f2-c0272fe6a352",
  //         "is_system": true,
  //         "label": "Unknown",
  //         "created_at": "2025-11-30T21:39:24.920771"
  //     },
  //     "property_customer_reference": "3245b097-c506-4d60-8615-6264f1c14e2b",
  //     "coordinates": {
  //         "latitude": 53.58436225297437,
  //         "longitude": -2.210266880526774
  //     },
  //     "created_at": "2025-11-30T21:39:25.206944",
  //     "geometry": null,
  //     "address": {
  //         "country": "England",
  //         "town": "Heywood",
  //         "postcode": "OL10 2PN",
  //         "street": "Manchester Road"
  //     },
  //     "attributes": [],
  //     "groups": [],
  //     "insurance": {
  //         "value": 3000000,
  //     },
  //     "metadata": {
  //         "distance": null
  //     }
  // }

  useEffect(() => {
    dispatch(systemActions.setPropertyId(Number.parseInt(propertyId)))
  }, [propertyId])

  const goToEditProperty = () => {
    history.push(siteMap.EditProperty.getPath(propertyId))
  }

  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget)
  }

  const handleCloseActionsMenu = () => {
    if (isDeleting) return
    setActionsAnchorEl(null)
  }

  const handleOpenDeleteDialog = () => {
    setActionsAnchorEl(null)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return
    setDeleteDialogOpen(false)
  }

  const handleDeleteProperty = async () => {
    setIsDeleting(true)

    try {
      await api.delete(`/locations/${propertyId}`)
      enqueueSnackbar('Property deleted', { variant: 'success' })
      history.push(`${siteMap.Organisation.path}/properties`)
    } catch (err: any) {
      console.error(err)
      enqueueSnackbar(err?.response?.data?.detail || err?.message || 'Failed to delete property', {
        variant: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAddMitigation = () => {
    history.push(
      siteMap.FormList.getPath({
        typeID: 5,
        title: 'Add Mitigations',
        propertyID: propertyId,
      }),
    )
  }

  const handleCompleteChecklist = () => {
    history.push(
      siteMap.FormList.getPath({
        typeID: 7,
        title: 'Complete Checklist',
        propertyID: propertyId,
      }),
    )
  }

  const propertyAddress = [property?.address?.street, property?.address?.town, property?.address?.postcode]
    .filter(Boolean)
    .join(', ')
  const propertyReference = property?.property_customer_reference?.trim()
  const propertyName = property?.name ?? 'Unnamed property'
  const canEditProperty = possible(PropertyPermissions.Edit)
  const canDeleteProperty = possible(PropertyPermissions.Delete)
  const showPropertyActions = canEditProperty || canDeleteProperty

  return (
    <Page
      title={property?.name ?? null}
      actions={
        <>
          {showPropertyActions && (
            <IconButton aria-label="Property actions" onClick={handleOpenActionsMenu}>
              <MoreVertIcon />
            </IconButton>
          )}
          <Menu
            anchorEl={actionsAnchorEl}
            open={Boolean(actionsAnchorEl)}
            onClose={handleCloseActionsMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{ dense: true }}>
            {canEditProperty && (
              <MenuItem onClick={goToEditProperty}>
                <ListItemIcon>
                  <EditOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Edit property
              </MenuItem>
            )}
            {canDeleteProperty && (
              <MenuItem onClick={handleOpenDeleteDialog}>
                <ListItemIcon>
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                Delete property
              </MenuItem>
            )}
          </Menu>
          <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
            <DialogTitle>Delete property?</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {propertyName}
              </Typography>
              {propertyReference && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Reference: {propertyReference}
                </Typography>
              )}
              {propertyAddress && (
                <Typography variant="body2" color="text.secondary">
                  Address: {propertyAddress}
                </Typography>
              )}
              <Typography sx={{ mt: 2 }}>This action cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
                Cancel
              </Button>
              <LoadingButton onClick={handleDeleteProperty} variant="contained" color="error" loading={isDeleting}>
                Delete
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </>
      }>
      <Grid container spacing={2}>
        {/* Map */}
        <Grid item minWidth={300} xs={0} md lg={7} sx={{ display: { xs: 'none', md: 'block' } }}>
          <DashboardMap propertyId={propertyId} />
        </Grid>

        <Grid item md lg={5}>
          <Stack spacing={2}>
            {/* Climate Hazards */}
            <FeatureGate feature="projections">
              <PropertyClimateHazardsCard propertyId={propertyId} />
            </FeatureGate>
            {/* Alerts Card */}
            <FeatureGate feature="alerts">
              <AlertsCard propertyId={propertyId} />
            </FeatureGate>

            {/* Actions Card */}
            <Card>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2 }}
                alignItems={{ sm: 'center' }}
                useFlexGap>
                <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                  Open Forms
                </Typography>

                {/* Button group */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ ml: { sm: 1 } }}>
                  <Button variant="outlined" size="small" className="button-drafts-1" onClick={handleCompleteChecklist}>
                    <AddRoundedIcon sx={{ width: 16, height: 16 }} />
                    Checklist
                  </Button>
                  <Button variant="outlined" size="small" className="button-drafts-1" onClick={handleAddMitigation}>
                    <AddRoundedIcon sx={{ width: 16, height: 16 }} />
                    Mitigation
                  </Button>
                  {/* add more buttons if needed */}
                </Stack>
              </Stack>

              <hr style={{ marginBottom: 0 }}></hr>
              <SavedDrafts property_id={propertyId} />
            </Card>

            {/* Activity Card */}
            <ActivityCard property_id={propertyId} />
          </Stack>
        </Grid>
      </Grid>
    </Page>
  )
}

export default Property
